import { supabase } from "@/integrations/supabase/client";
import type { WorkspaceTask } from "@/hooks/useWorkspaceTasks";

const TASK_DETAIL_SELECT = `id, workspace_id, title, description, due_date, due_at, status, outcome,
  completed_at, assigned_member_id, created_by_member_id, updated_at, parent_task_id,
  priority, lead_id, tags, project_id, project_column_id, board_order_index,
  assignee:assigned_member_id(display_name),
  creator:created_by_member_id(display_name),
  lead:lead_id(id, name)`;

export interface TaskBreadcrumbItem {
  id: string;
  label: string;
  kind: "project" | "task";
}

export async function fetchWorkspaceTaskDetail(
  taskId: string,
): Promise<WorkspaceTask | null> {
  const { data, error } = await supabase
    .from("workspace_tasks")
    .select(TASK_DETAIL_SELECT)
    .eq("id", taskId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as WorkspaceTask | null;
}

async function fetchTaskNode(
  taskId: string,
): Promise<{ id: string; title: string; parent_task_id: string | null; project_id: string | null } | null> {
  const { data, error } = await supabase
    .from("workspace_tasks")
    .select("id, title, parent_task_id, project_id")
    .eq("id", taskId)
    .maybeSingle();
  if (error) throw error;
  return data as {
    id: string;
    title: string;
    parent_task_id: string | null;
    project_id: string | null;
  } | null;
}

export async function buildTaskBreadcrumbs(
  taskId: string,
  projectName: string,
): Promise<TaskBreadcrumbItem[]> {
  const chain: TaskBreadcrumbItem[] = [];
  let currentId: string | null = taskId;

  while (currentId) {
    const node = await fetchTaskNode(currentId);
    if (!node) break;
    chain.unshift({ id: node.id, label: node.title, kind: "task" });
    currentId = node.parent_task_id;
  }

  return [{ id: "project", label: projectName, kind: "project" }, ...chain];
}
